import type { GeocodeHit } from './geocoding';
import { geocode } from './geocoding';
import type {
  AvoidFeature,
  LatLng,
  Profile,
  RoadworkKind,
  RouteOptions,
  RoutePreference,
  ViewMode,
} from './types';
import { VIEW_MODE_LABELS } from './types';
import {
  t,
  getLang,
  onLangChange,
  formatDistance,
  formatDuration,
  formatSpeed,
  formatEtaClock,
} from './i18n';
import { maneuverArrowSvg, maneuverStreetLine, maneuverThenLabel, laneHintFromStep, resolveRoundaboutExit } from './maneuver';
import { yieldToMain } from './mainThread';
import { loadFavorites, loadLastRoute, loadSavedRouteOptions, placeDisplayName, saveRouteOptions } from './storage';
import type { SavedPlace } from './storage';

/**
 * Thin DOM binding layer. The element ids referenced here are declared in
 * `index.html`. UI logic in `main.ts` only talks to UiController — never
 * touches the DOM directly.
 *
 * All user-visible strings are pulled via the `t(key)` helper (English).
 */

// ---------------------------------------------------------------------------
// Public controller interface
// ---------------------------------------------------------------------------

export type RouteStep = {
  instruction: string;
  name: string;
  distance: number;
  duration: number;
  type?: number;
  exit_number?: number;
};

/** Visual / logical state of the live-navigation toggle button. */
export type NavState = 'hidden' | 'planned' | 'navigating' | 'paused';

/**
 * Live data shown in the bottom HUD strip during an active navigation
 * session. All values arrive as units; i18n.format* helpers render the
 * language-aware strings.
 */
export interface HudData {
  speedMps: number | null;
  remainingDistance: number;
  remainingDuration: number;
  eta: Date | null;
  currentStep: {
    instruction: string;
    name: string;
    remainingStepMeters: number;
    type?: number;
    exit_number?: number;
  } | null;
  /** Upcoming maneuver after the current one (index+1). */
  nextStep: {
    instruction: string;
    name: string;
    type?: number;
  } | null;
  traveledMeters: number;
  totalMeters: number;
}

/**
 * Structured input for `setHudRadar` — replaces the old `string | null`
 * API so the radar chip can be re-translated on a language switch
 * without round-tripping through main.ts. Distance is in metres.
 */
export interface RadarChip {
  dist: number;
  /** Camera-enforced maxspeed if OSM has one on the node. */
  maxspeed?: number;
}

interface EventMap {
  changeProfile: (profile: Profile) => void;
  search: (query: string) => void;
  searchResult: (hit: GeocodeHit) => void;
  autozoom: () => void;
  clearRoute: () => void;
  navtoggle: () => void;
  pausetoggle: () => void;
  overview: () => void;
  /**
   * The new view mode, as carried by the topbar button's `data-view`
   * attribute. Passed as a payload — do NOT re-derive it in main.ts
   * (the previous `nextViewMode(viewMode)` silently inverted the
   * persisted preference on every cold-boot).
   */
  viewmode: (mode: ViewMode) => void;
  routeOptions: (opts: RouteOptions) => void;
  layout: () => void;
  panelToggle: (expanded: boolean) => void;
  favoriteGo: (place: { lat: number; lng: number; label: string }) => void;
  favoriteSet: (
    slot: 'home' | 'work',
    place?: { lat: number; lng: number; label: string },
  ) => void;
  favoriteSave: (place: { lat: number; lng: number; label: string; name?: string }) => void;
  favoriteRemove: (place: { lat: number; lng: number; label: string; name?: string }) => void;
  favoriteClearSlot: (slot: 'home' | 'work') => void;
  favoriteRename: (place: { lat: number; lng: number; label: string; name?: string }) => void;
  restoreLastRoute: () => void;
  shareDestination: () => void;
  addStop: () => void;
  removeStop: (index: number) => void;
  powerSaver: (on: boolean) => void;
}

export interface UiController {
  setRoute(
    coords: LatLng[],
    distance: number,
    duration: number,
    steps: RouteStep[],
  ): void;
  clearRoute(): void;
  setStatus(message: string, tone?: 'info' | 'error'): void;
  setNavState(state: NavState): void;
  setInfoExpanded(expanded: boolean): void;
  setHudData(data: HudData): void;
  setSpeedLimit(kmh: number | null): void;
  setHudRadar(parts: RadarChip | null): void;
  setRoadwork(kind: RoadworkKind | null, distanceM: number | null): void;
  setOverspeed(isOver: boolean): void;
  setViewMode(mode: ViewMode): void;
  setAutoZoomActive(active: boolean): void;
  setArrowBearing(bearingDeg: number): void;
  setWeather(data: {
    tempLabel: string;
    windLabel: string;
    windTowardDeg: number;
  } | null): void;
  setRouteOptionsUi(opts: RouteOptions, profile: Profile): void;
  refreshFavorites(): void;
  setRestoreVisible(visible: boolean): void;
  setHudAmenities(
    next: Partial<Record<'fuel' | 'toilet' | 'rest', { distM: number }>>,
  ): void;
  setPowerSaverUi(on: boolean): void;
  setSearchQuery(value: string): void;
  setStopsUi(
    stops: Array<{ label: string }>,
    opts: { addingStop: boolean; canAdd: boolean },
  ): void;
  setPowerDim(on: boolean): void;
  on<K extends keyof EventMap>(event: K, handler: EventMap[K]): void;
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export function mountUi(): UiController {
  const el = <T extends HTMLElement = HTMLElement>(id: string): T => {
    const node = document.getElementById(id);
    if (!node) throw new Error(`Hiányzó DOM elem: #${id}`);
    return node as T;
  };

  // ----- DOM bindings (declared once) -----
  const searchForm = el<HTMLFormElement>('search');
  const searchInput = el<HTMLInputElement>('search-input');
  const searchClear = el<HTMLButtonElement>('search-clear');
  const profileButtons = Array.from(
    document.querySelectorAll<HTMLButtonElement>('.profile'),
  );
  const info = el('info');
  const infoDistance = el('info-distance');
  const infoDuration = el('info-duration');
  const infoSteps = el<HTMLOListElement>('info-steps');
  const infoClear = el<HTMLButtonElement>('info-clear');
  const infoMessage = el('info-message');
  const infoCollapse = el<HTMLButtonElement>('info-collapse');
  const infoExpand = el<HTMLButtonElement>('info-expand');
  const recenterBtn = el<HTMLButtonElement>('recenter');
  const weatherChip = el('weather-chip');
  const weatherTemp = el('weather-temp');
  const weatherWindSpeed = el('weather-wind-speed');
  const weatherWindArrow = el('weather-wind-arrow');
  const navtoggle = el<HTMLButtonElement>('nav-toggle');
  const hud = el('hud');
  const hudNext = el('hud-next');
  const hudNextInstr = el('hud-next-instr');
  const hudNextName = el('hud-next-name');
  const hudNextDist = el<HTMLSpanElement>('hud-next-dist');
  const hudNextArrow = el<HTMLDivElement>('hud-next-arrow');
  const hudSpeed = el('hud-speed');
  const hudDistance = el('hud-distance');
  const hudDuration = el('hud-duration');
  const hudEta = el('hud-eta');
  const hudSpeedLabel = hudSpeed.closest('.hud-stat')?.querySelector<HTMLElement>('.hud-label');
  const hudDistanceLabel = hudDistance.closest('.hud-stat')?.querySelector<HTMLElement>('.hud-label');
  const hudDurationLabel = hudDuration.closest('.hud-stat')?.querySelector<HTMLElement>('.hud-label');
  const hudEtaLabel = hudEta.closest('.hud-stat')?.querySelector<HTMLElement>('.hud-label');
  const infoDistanceLabel = infoDistance.parentElement?.querySelector<HTMLElement>('.info-label');
  const infoDurationLabel = infoDuration.parentElement?.querySelector<HTMLElement>('.info-label');
  const hudExpand = el<HTMLButtonElement>('hud-expand');
  const chromeStop = el<HTMLButtonElement>('chrome-stop');
  const hudOverview = el<HTMLButtonElement>('hud-overview');
  const hudProgress = el<HTMLDivElement>('hud-progress');
  const hudProgressFill = el<HTMLDivElement>('hud-progress-fill');
  const hudProgressMarker = el<HTMLDivElement>('hud-progress-marker');
  const hudProgressDone = el<HTMLSpanElement>('hud-progress-done');
  const hudProgressLeft = el<HTMLSpanElement>('hud-progress-left');
  const hudLimit = el<HTMLSpanElement>('hud-limit');
  const hudRadar = el<HTMLDivElement>('hud-radar');
  const hudRadarText = el<HTMLSpanElement>('hud-radar-text');
  const hudRadarDist = el<HTMLSpanElement>('hud-radar-dist');
  const hudRoadwork = el<HTMLDivElement>('hud-roadwork');
  const hudRoadworkIcon = el<HTMLSpanElement>('hud-roadwork-icon');
  const hudRoadworkText = el<HTMLSpanElement>('hud-roadwork-text');
  const hudRoadworkDist = el<HTMLSpanElement>('hud-roadwork-dist');
  const hudRouteOpts = el<HTMLButtonElement>('hud-route-opts');
  const infoRouteOpts = el<HTMLButtonElement>('info-route-opts');
  const infoShare = el<HTMLButtonElement>('info-share');
  const infoItinerary = el('info-itinerary');
  const infoItineraryHead = el('info-itinerary-head');
  const infoAddStop = el<HTMLButtonElement>('info-add-stop');
  const infoStops = el<HTMLOListElement>('info-stops');
  let addingStopUi = false;
  const routeOptsPanel = el<HTMLDivElement>('route-opts');
  const favToggle = el<HTMLButtonElement>('fav-toggle');
  const favPanel = el<HTMLDivElement>('fav-panel');
  const favHome = el<HTMLButtonElement>('fav-home');
  const favWork = el<HTMLButtonElement>('fav-work');
  const favHomeSub = el<HTMLSpanElement>('fav-home-sub');
  const favWorkSub = el<HTMLSpanElement>('fav-work-sub');
  const favHomeAssign = el<HTMLButtonElement>('fav-home-assign');
  const favWorkAssign = el<HTMLButtonElement>('fav-work-assign');
  const favHomeClear = el<HTMLButtonElement>('fav-home-clear');
  const favWorkClear = el<HTMLButtonElement>('fav-work-clear');
  const favSaved = el<HTMLDivElement>('fav-saved');
  const favRecent = el<HTMLDivElement>('fav-recent');
  const favRestore = el<HTMLButtonElement>('fav-restore');
  const favRestoreLabel = el<HTMLSpanElement>('fav-restore-label');
  const favSavedHeading = el('fav-saved-heading');
  const favRecentHeading = el('fav-recent-heading');
  const hitFavSheet = el<HTMLDivElement>('hit-fav-sheet');
  const hudLaneHint = el<HTMLSpanElement>('hud-lane-hint');
  const hudThen = el<HTMLDivElement>('hud-then');
  const hudThenArrow = el<HTMLSpanElement>('hud-then-arrow');
  const hudThenInstr = el<HTMLSpanElement>('hud-then-instr');
  const hudThenPrefix = hudThen.querySelector<HTMLElement>('.maneuver-then-prefix');
  const powerSaverBtn = el<HTMLButtonElement>('power-saver');
  const powerDimEl = el<HTMLDivElement>('power-dim');
  const hudAmenities = el<HTMLDivElement>('hud-amenities');
  const hudAmenityFuel = el<HTMLSpanElement>('hud-amenity-fuel');
  const hudAmenityToilet = el<HTMLSpanElement>('hud-amenity-toilet');
  const hudAmenityRest = el<HTMLSpanElement>('hud-amenity-rest');
  const viewButtons = Array.from(
    document.querySelectorAll<HTMLButtonElement>('.view-btn[data-view]'),
  );
  const userArrow = el<HTMLDivElement>('user-arrow');
  const mapCompass = el<HTMLButtonElement>('map-compass');
  const mapCompassRose = mapCompass.querySelector<HTMLElement>('.map-compass-rose');

  let routeOpts: RouteOptions = loadSavedRouteOptions();
  let routeOptsProfile: Profile = 'driving-car';
  let routeOptsOpen = false;
  let autoZoomActive = true;
  let powerSaverOn = false;
  let pendingHitPlace: SavedPlace | null = null;

  function promptFavoriteName(place: SavedPlace): string | null {
    const suggested = place.name?.trim() || place.label.split(',')[0]?.trim() || place.label;
    const raw = window.prompt(t('fav.namePrompt'), suggested);
    if (raw === null) return null;
    return raw.trim();
  }

  function popoverOpen(node: HTMLElement): void {
    const pop = node as HTMLElement & { showPopover?: () => void };
    if (typeof pop.showPopover === 'function') {
      try {
        pop.showPopover();
        delete node.dataset.open;
        return;
      } catch {
        /* fall through */
      }
    }
    node.dataset.open = '1';
  }

  function popoverClose(node: HTMLElement): void {
    const pop = node as HTMLElement & { hidePopover?: () => void };
    if (typeof pop.hidePopover === 'function') {
      try {
        pop.hidePopover();
      } catch {
        /* ignore */
      }
    }
    delete node.dataset.open;
  }

  function popoverIsOpen(node: HTMLElement): boolean {
    if (node.dataset.open === '1') return true;
    try {
      return node.matches(':popover-open');
    } catch {
      return false;
    }
  }

  function paintAmenityChip(
    elChip: HTMLSpanElement,
    distM: number | undefined,
  ): void {
    const distEl = elChip.querySelector<HTMLElement>('.hud-amenity-dist');
    if (distM === undefined || !Number.isFinite(distM)) {
      elChip.hidden = true;
      if (distEl) distEl.textContent = '';
      return;
    }
    elChip.hidden = false;
    if (distEl) distEl.textContent = formatDistance(distM);
  }

  function amenityPaintSig(
    next: Partial<Record<'fuel' | 'toilet' | 'rest', { distM: number }>>,
  ): string {
    return [
      next.fuel ? formatDistance(next.fuel.distM) : '',
      next.toilet ? formatDistance(next.toilet.distM) : '',
      next.rest ? formatDistance(next.rest.distM) : '',
    ].join('|');
  }

  function applyHudAmenities(
    next: Partial<Record<'fuel' | 'toilet' | 'rest', { distM: number }>>,
  ): void {
    const sig = amenityPaintSig(next);
    if (sig === lastAmenityPaintSig) return;
    lastAmenityPaintSig = sig;
    paintAmenityChip(hudAmenityFuel, next.fuel?.distM);
    paintAmenityChip(hudAmenityToilet, next.toilet?.distM);
    paintAmenityChip(hudAmenityRest, next.rest?.distM);
    hudAmenities.hidden = !next.fuel && !next.toilet && !next.rest;
  }

  function applyPowerSaverButton(on: boolean): void {
    powerSaverOn = on;
    document.body.dataset.powerSaver = on ? '1' : '0';
    powerSaverBtn.classList.toggle('is-active', on);
    powerSaverBtn.setAttribute('aria-pressed', String(on));
    powerSaverBtn.setAttribute('aria-label', t('power.saver'));
    powerSaverBtn.title = on ? t('power.saverOn') : t('power.saverOff');
    if (!on) {
      powerDimEl.hidden = true;
      document.body.dataset.powerDim = '0';
    }
  }

  function applyPowerDim(on: boolean): void {
    if (!powerSaverOn) {
      powerDimEl.hidden = true;
      document.body.dataset.powerDim = '0';
      return;
    }
    powerDimEl.hidden = !on;
    document.body.dataset.powerDim = on ? '1' : '0';
  }

  // Hits overlay — fixed under the topbar so it never participates in
  // body's 2-row grid (which previously stole the map's 1fr row and ate
  // pointer events when suggestions appeared).
  const topbar = document.querySelector<HTMLElement>('.topbar');
  const profilesEl = document.querySelector<HTMLElement>('.profiles');
  const hitsBox = document.createElement('div');
  hitsBox.className = 'hits';
  hitsBox.id = 'hits';
  hitsBox.setAttribute('role', 'listbox');
  hitsBox.hidden = true;
  document.body.appendChild(hitsBox);

  function placeHitsUnderTopbar(): void {
    if (!topbar) return;
    const rect = topbar.getBoundingClientRect();
    hitsBox.style.top = `${Math.round(rect.bottom + 4)}px`;
    if (document.body.dataset.landscape === '1') {
      hitsBox.style.left = '';
      hitsBox.style.right = '8px';
      hitsBox.style.width = `${Math.round(rect.width - 16)}px`;
    } else {
      hitsBox.style.left = '';
      hitsBox.style.right = '';
      hitsBox.style.width = '';
    }
  }

  function syncLandscapeLayout(): void {
    const landscape = window.matchMedia(
      '(orientation: landscape) and (max-height: 700px)',
    ).matches;
    document.body.dataset.landscape = landscape ? '1' : '0';
    if (landscape) {
      const vw = Math.max(1, window.innerWidth);
      const rail = Math.round(Math.min(vw * 0.4, 360));
      const mapW = Math.max(160, vw - rail);
      document.documentElement.style.setProperty('--rail-w', `${rail}px`);
      document.documentElement.style.setProperty('--map-pane-w', `${mapW}px`);
      const topH = topbar ? Math.round(topbar.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty(
        '--landscape-topbar-h',
        `${String(Math.max(56, topH))}px`,
      );
    } else {
      document.documentElement.style.removeProperty('--rail-w');
      document.documentElement.style.removeProperty('--map-pane-w');
      document.documentElement.style.removeProperty('--landscape-topbar-h');
    }
    syncManeuverTop();
    syncFabLift();
    if (!hitsBox.hidden) placeHitsUnderTopbar();
    if (popoverIsOpen(favPanel)) placeFavPanel();
    notifyLayout();
  }

  const handlers: { [K in keyof EventMap]?: EventMap[K] } = {};

  // ----- Source-of-truth mirrors so a language switch can replay chip
  //       text without main.ts having to push everything every time.
  let lastNavState: NavState = 'hidden';
  let lastViewMode: ViewMode = 'north';
  let lastHudData: HudData | null = null;
  let lastHudPaintSig = '';
  let lastAmenityPaintSig = '';
  /** Cancels in-flight step list paints when a newer route arrives. */
  let stepsPaintToken = 0;
  /** Last radar chip input (structured). Re-rendered with new locale. */
  let lastRadar: RadarChip | null = null;
  /** Last roadwork chip input. Re-rendered with new locale. */
  let lastRoadwork: { kind: RoadworkKind | null; distanceM: number | null } | null = null;
  /** Last posted speed-limit value (km/h). Universal symbol; just a refresh. */
  let lastSpeedLimitKmh: number | null = null;
  /** Last overspeed flag — re-applies the CSS class on lang switch. */
  let lastOverspeed = false;

  // -------------------------------------------------------------------
  // Per-chip applicators — captured here so applyStaticLabels(), and
  // each public setter, share the same rendering logic.
  // -------------------------------------------------------------------
  function applyRadarChipFromCache(): void {
    if (!lastRadar) {
      hudRadar.hidden = true;
      hudRadarText.textContent = '';
      hudRadarDist.textContent = '';
      return;
    }
    const label = t('chip.radar.label');
    const distText = `${lastRadar.dist} m`;
    if (lastRadar.maxspeed !== undefined) {
      hudRadarText.textContent = `${label} \u2264${lastRadar.maxspeed}`;
      hudRadarDist.textContent = distText;
    } else {
      hudRadarText.textContent = label;
      hudRadarDist.textContent = distText;
    }
    hudRadar.hidden = false;
  }

  function applyRoadworkChip(kind: RoadworkKind | null, distanceM: number | null): void {
    if (kind === null || distanceM === null || !Number.isFinite(distanceM)) {
      hudRoadworkReset();
      return;
    }
    hudRoadwork.classList.remove('is-construction', 'is-closure');
    hudRoadwork.classList.add(kind === 'closure' ? 'is-closure' : 'is-construction');
    hudRoadworkIcon.textContent = kind === 'closure' ? '\u26d4' : '\u26a0\ufe0f';
    hudRoadworkText.textContent =
      kind === 'closure' ? t('chip.roadwork.closure') : t('chip.roadwork.construction');
    hudRoadworkDist.textContent = `${Math.round(distanceM)} m`;
    hudRoadwork.hidden = false;
  }

  function applyRoadworkChipFromCache(): void {
    if (!lastRoadwork) {
      hudRoadworkReset();
      return;
    }
    applyRoadworkChip(lastRoadwork.kind, lastRoadwork.distanceM);
  }

  function applySpeedLimitFromCache(): void {
    if (lastSpeedLimitKmh === null || !Number.isFinite(lastSpeedLimitKmh) || lastSpeedLimitKmh <= 0) {
      hudLimit.hidden = true;
      hudLimit.textContent = '';
      return;
    }
    hudLimit.hidden = false;
    hudLimit.textContent = String(Math.round(lastSpeedLimitKmh));
  }

  function applyOverspeedFromCache(): void {
    hudSpeed.classList.toggle('is-overspeed', lastOverspeed);
  }

  /** Local helper — uses captured DOM bindings for encapsulation. */
  function hudRoadworkReset(): void {
    hudRoadwork.hidden = true;
    hudRoadwork.classList.remove('is-construction', 'is-closure');
    hudRoadworkIcon.textContent = '';
    hudRoadworkText.textContent = '';
    hudRoadworkDist.textContent = '';
  }

  // -------------------------------------------------------------------
  // Static label applicator (runs once on mount + on every lang change)
  // -------------------------------------------------------------------
  function applyStaticLabels(): void {
    document.body.dataset.lang = getLang();

    searchInput.placeholder = addingStopUi
      ? t('search.addStopPlaceholder')
      : t('search.placeholder');
    searchForm.setAttribute('aria-label', searchInput.placeholder);
    searchClear.setAttribute('aria-label', t('search.clear'));

    for (const btn of profileButtons) {
      const profile = btn.dataset.profile as Profile | undefined;
      if (!profile) continue;
      const key =
        profile === 'driving-car'
          ? 'profile.driving'
          : profile === 'cycling-regular'
            ? 'profile.cycling'
            : 'profile.walking';
      const labelEl = btn.querySelector<HTMLElement>('.profile-label');
      const translated = t(key);
      if (translated !== key && labelEl) labelEl.textContent = translated;
    }

    if (infoDistanceLabel) infoDistanceLabel.textContent = t('info.distance');
    if (infoDurationLabel) infoDurationLabel.textContent = t('info.time');
    infoClear.setAttribute('aria-label', t('info.clear'));
    infoCollapse.setAttribute('aria-label', t('info.collapse'));
    infoCollapse.textContent = t('info.collapse');
    infoExpand.setAttribute('aria-label', t('info.expand'));
    infoExpand.textContent = t('info.expand');

    applyAutoZoomButton(autoZoomActive);

    if (hudSpeedLabel) hudSpeedLabel.textContent = t('hud.speed');
    if (hudDistanceLabel) hudDistanceLabel.textContent = t('hud.distance');
    if (hudDurationLabel) hudDurationLabel.textContent = t('hud.time');
    if (hudEtaLabel) hudEtaLabel.textContent = t('hud.eta');

    applyNavToggleButton(lastNavState);
    applyHudOverviewButton();
    applyHudExpandButton();
    applyChromeStopButton();
    applyViewModeButton(lastViewMode);
    paintFavorites();
    infoShare.textContent = t('info.share');
    infoShare.setAttribute('aria-label', t('info.shareHint'));
    infoItineraryHead.textContent = t('info.stops');
    infoAddStop.textContent = addingStopUi ? t('info.addStopArmed') : t('info.addStop');
    infoAddStop.setAttribute('aria-label', t('info.addStopHint'));
    infoStops.setAttribute('aria-label', t('info.stops'));
    if (hudThenPrefix) hudThenPrefix.textContent = t('hud.then');
    mapCompass.setAttribute('aria-label', t('map.compassHint'));
    mapCompass.title = t('map.compassHint');
    weatherChip.setAttribute('aria-label', t('weather.chip'));
    weatherChip.title = t('weather.chipTitle');
    applyPowerSaverButton(powerSaverOn);
    applyFavChromeLabels();
    applyRouteOptsLabels();
    paintRouteOptsPanel();
    applyHudRouteOptsButton();
    applyInfoRouteOptsButton();

    // Replay cached dynamic snapshots so unit suffixes + free-form
    // chips also update on lang switch.
    if (lastHudData) applyHudData(lastHudData);
    applyRadarChipFromCache();
    applyRoadworkChipFromCache();
    applySpeedLimitFromCache();
    applyOverspeedFromCache();
  }

  function applyNavToggleButton(state: NavState): void {
    lastNavState = state;
    const stopVisible = state === 'navigating' || state === 'paused';
    chromeStop.hidden = !stopVisible;
    switch (state) {
      case 'hidden':
        navtoggle.hidden = true;
        break;
      case 'planned':
        navtoggle.hidden = false;
        navtoggle.className = 'nav-toggle planned';
        navtoggle.textContent = t('nav.start');
        navtoggle.setAttribute('aria-label', t('nav.start'));
        break;
      case 'navigating':
        navtoggle.hidden = true;
        break;
      case 'paused':
        navtoggle.hidden = false;
        navtoggle.className = 'nav-toggle navigating';
        navtoggle.textContent = t('nav.resume');
        navtoggle.setAttribute('aria-label', t('nav.resume'));
        break;
    }
  }

  function applyHudOverviewButton(): void {
    hudOverview.innerHTML = `<span aria-hidden="true">\u25a6</span><span class="hud-action-label">${t('hud.action.overview')}</span>`;
    hudOverview.setAttribute('aria-label', t('hud.action.overviewHint'));
    hudOverview.title = t('hud.action.overviewHint');
  }

  function applyChromeStopButton(): void {
    const label = chromeStop.querySelector<HTMLElement>('.chrome-stop-label');
    if (label) label.textContent = t('hud.action.stop');
    chromeStop.setAttribute('aria-label', t('hud.action.stop'));
  }

  function applyHudExpandButton(): void {
    hudExpand.innerHTML = `<span aria-hidden="true">\u2303</span><span class="hud-action-label">${t('hud.action.expand')}</span>`;
    hudExpand.setAttribute('aria-label', t('hud.action.expand'));
  }

  function applyViewModeButton(mode: ViewMode): void {
    lastViewMode = mode;
    document.body.dataset.viewMode = mode;

    for (const btn of viewButtons) {
      const btnMode = btn.dataset.view as ViewMode | undefined;
      const active = btnMode === mode;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
      if (!btnMode) continue;
      const cfg = VIEW_MODE_LABELS[btnMode];
      const key =
        btnMode === 'north'
          ? 'hud.view.north'
          : btnMode === '2d'
            ? 'hud.view.2d'
            : 'hud.view.tilted';
      const label = t(key);
      btn.setAttribute('aria-label', label);
      btn.title = label;
      const iconEl = btn.querySelector<HTMLElement>('.view-btn-icon');
      if (iconEl) iconEl.textContent = cfg.icon;
    }
    syncMapCompassVisibility(mode);
  }

  function syncMapCompassVisibility(mode: ViewMode): void {
    const show = mode === '2d' || mode === 'tilted';
    mapCompass.hidden = !show;
    mapCompass.setAttribute('aria-hidden', show ? 'false' : 'true');
    syncFabLift();
  }

  function applyAutoZoomButton(active: boolean): void {
    autoZoomActive = active;
    recenterBtn.classList.toggle('is-active', active);
    recenterBtn.setAttribute('aria-pressed', String(active));
    const icon = recenterBtn.querySelector<HTMLElement>('.recenter-icon');
    if (icon) icon.textContent = active ? '\u25ce' : '\u25c9';
    recenterBtn.setAttribute('aria-label', t('hud.action.autozoomHint'));
    recenterBtn.title = t('hud.action.autozoomHint');
  }

  function applyHudRouteOptsButton(): void {
    hudRouteOpts.innerHTML = `<span aria-hidden="true">\u2699</span><span class="hud-action-label">${t('hud.action.routeOpts')}</span>`;
    hudRouteOpts.setAttribute('aria-label', t('hud.action.routeOptsHint'));
    hudRouteOpts.title = t('hud.action.routeOptsHint');
    hudRouteOpts.setAttribute('aria-expanded', String(routeOptsOpen));
  }

  function applyInfoRouteOptsButton(): void {
    infoRouteOpts.textContent = `\u2699 ${t('hud.action.routeOpts')}`;
    infoRouteOpts.setAttribute('aria-label', t('hud.action.routeOptsHint'));
    infoRouteOpts.setAttribute('aria-expanded', String(routeOptsOpen));
  }

  function applyRouteOptsLabels(): void {
    const prefLabel = document.getElementById('route-opts-pref-label');
    const avoidLabel = document.getElementById('route-opts-avoid-label');
    if (prefLabel) prefLabel.textContent = t('route.opts.preference');
    if (avoidLabel) avoidLabel.textContent = t('route.opts.avoid');
    routeOptsPanel.setAttribute('aria-label', t('route.opts.title'));
    for (const btn of routeOptsPanel.querySelectorAll<HTMLButtonElement>('[data-pref]')) {
      const pref = btn.dataset.pref as RoutePreference | undefined;
      if (!pref) continue;
      const key =
        pref === 'recommended'
          ? 'route.opts.recommended'
          : pref === 'fastest'
            ? 'route.opts.fastest'
            : 'route.opts.shortest';
      btn.textContent = t(key);
    }
    for (const label of routeOptsPanel.querySelectorAll<HTMLLabelElement>('.route-opts-check')) {
      const input = label.querySelector<HTMLInputElement>('input[data-avoid]');
      const span = label.querySelector('span');
      const avoid = input?.dataset.avoid as AvoidFeature | undefined;
      if (!span || !avoid) continue;
      const key =
        avoid === 'highways'
          ? 'route.opts.highways'
          : avoid === 'ferries'
            ? 'route.opts.ferries'
            : 'route.opts.tollways';
      span.textContent = t(key);
    }
  }

  function paintRouteOptsPanel(): void {
    for (const btn of routeOptsPanel.querySelectorAll<HTMLButtonElement>('[data-pref]')) {
      btn.classList.toggle('is-active', btn.dataset.pref === routeOpts.preference);
    }
    for (const input of routeOptsPanel.querySelectorAll<HTMLInputElement>('input[data-avoid]')) {
      const feature = input.dataset.avoid as AvoidFeature | undefined;
      if (!feature) continue;
      input.checked = routeOpts.avoid.includes(feature);
    }
    const drivingOnly = routeOptsProfile === 'driving-car';
    for (const label of routeOptsPanel.querySelectorAll<HTMLLabelElement>('[data-avoid-for="driving"]')) {
      label.dataset.avoidHidden = drivingOnly ? '0' : '1';
    }
  }

  function setRouteOptsOpen(open: boolean): void {
    routeOptsOpen = open;
    routeOptsPanel.hidden = !open;
    applyHudRouteOptsButton();
    applyInfoRouteOptsButton();
    if (open) {
      paintRouteOptsPanel();
      if (document.body.dataset.landscape === '1') {
        const topbarRect = topbar?.getBoundingClientRect();
        const top = topbarRect ? Math.round(topbarRect.bottom + 8) : 72;
        routeOptsPanel.style.top = `${top}px`;
        routeOptsPanel.style.bottom = 'auto';
      } else {
        routeOptsPanel.style.top = '';
        const hudRect = document.getElementById('hud')?.getBoundingClientRect();
        const infoRect = document.getElementById('info')?.getBoundingClientRect();
        const bottomChrome = Math.max(
          hudRect && !document.getElementById('hud')?.hidden ? window.innerHeight - hudRect.top : 0,
          infoRect && !document.getElementById('info')?.hidden && navState === 'planned'
            ? window.innerHeight - infoRect.top
            : 0,
          180,
        );
        routeOptsPanel.style.bottom = `calc(${Math.round(bottomChrome + 8)}px + var(--safe-bottom))`;
      }
      window.requestAnimationFrame(() => syncFabLift());
    } else {
      syncFabLift();
    }
  }

  function emitRouteOptions(): void {
    saveRouteOptions(routeOpts);
    handlers.routeOptions?.(routeOpts);
  }

  function hudPaintSig(data: HudData): string {
    const total = data.totalMeters;
    const traveled = Math.max(0, data.traveledMeters);
    const pct =
      total > 0 && Number.isFinite(total)
        ? Math.round(Math.min(100, Math.max(0, (traveled / total) * 100)))
        : 0;
    const stepDist =
      data.currentStep && data.currentStep.remainingStepMeters > 0
        ? formatDistance(data.currentStep.remainingStepMeters)
        : data.currentStep
          ? 'done'
          : '';
    return [
      formatSpeed(data.speedMps),
      formatDistance(data.remainingDistance),
      formatDuration(data.remainingDuration),
      formatEtaClock(data.eta),
      String(pct),
      data.currentStep?.instruction ?? '',
      data.currentStep?.name ?? '',
      String(data.currentStep?.type ?? ''),
      stepDist,
      data.nextStep?.instruction ?? '',
      data.nextStep?.name ?? '',
    ].join('\u001f');
  }

  function applyHudData(data: HudData): void {
    const sig = hudPaintSig(data);
    if (sig === lastHudPaintSig) return;
    lastHudPaintSig = sig;
    hudSpeed.textContent = formatSpeed(data.speedMps);
    hudDistance.textContent = formatDistance(data.remainingDistance);
    hudDuration.textContent = formatDuration(data.remainingDuration);
    hudEta.textContent = formatEtaClock(data.eta);
    const total = data.totalMeters;
    const traveled = Math.max(0, data.traveledMeters);
    const pct =
      total > 0 && Number.isFinite(total)
        ? Math.min(100, Math.max(0, (traveled / total) * 100))
        : 0;
    hudProgressFill.style.width = `${pct}%`;
    hudProgressMarker.style.left = `${pct}%`;
    hudProgress.setAttribute('aria-valuenow', String(Math.round(pct)));
    hudProgressDone.textContent = `${Math.round(pct)}%`;
    hudProgressLeft.textContent =
      data.remainingDistance > 0 ? formatDistance(data.remainingDistance) : '';
    if (data.currentStep && data.currentStep.instruction) {
      const { instruction, name, type, remainingStepMeters, exit_number } =
        data.currentStep;
      hudNextInstr.textContent = instruction;
      const street = maneuverStreetLine(instruction, name);
      if (street) {
        hudNextName.textContent = street;
        hudNextName.hidden = false;
      } else {
        hudNextName.textContent = '';
        hudNextName.hidden = true;
      }
      hudNextArrow.innerHTML = maneuverArrowSvg(type, instruction);
      if (remainingStepMeters > 0) {
        hudNextDist.textContent = formatDistance(remainingStepMeters);
      } else {
        hudNextDist.textContent = '\u2713';
      }

      const lane = laneHintFromStep(type, instruction, exit_number);
      const exitN = resolveRoundaboutExit(type, instruction, exit_number);
      if (lane === 'keep-left') {
        hudLaneHint.textContent = t('hud.lane.keepLeft');
        hudLaneHint.hidden = false;
      } else if (lane === 'keep-right') {
        hudLaneHint.textContent = t('hud.lane.keepRight');
        hudLaneHint.hidden = false;
      } else if (lane === 'exit' && exitN !== null) {
        hudLaneHint.textContent = t('hud.lane.exit').replace(
          '{n}',
          String(exitN),
        );
        hudLaneHint.hidden = false;
      } else {
        hudLaneHint.textContent = '';
        hudLaneHint.hidden = true;
      }

      if (data.nextStep?.instruction) {
        if (hudThenPrefix) hudThenPrefix.textContent = t('hud.then');
        hudThenInstr.textContent = maneuverThenLabel(data.nextStep.instruction);
        hudThenArrow.innerHTML = maneuverArrowSvg(
          data.nextStep.type,
          data.nextStep.instruction,
        );
        hudThen.hidden = false;
      } else {
        hudThen.hidden = true;
        hudThenInstr.textContent = '';
        hudThenArrow.replaceChildren();
      }

      hudNext.hidden = false;
      syncManeuverTop();
    } else {
      hudNext.hidden = true;
      hudLaneHint.hidden = true;
      hudThen.hidden = true;
    }
  }

  function applyFavChromeLabels(): void {
    favToggle.setAttribute('aria-label', t('fav.menu'));
    favToggle.title = t('fav.menu');
    favPanel.setAttribute('aria-label', t('fav.menu'));
    const homeTitle = favHome.querySelector<HTMLElement>('.fav-row-title');
    const workTitle = favWork.querySelector<HTMLElement>('.fav-row-title');
    if (homeTitle) homeTitle.textContent = t('fav.home');
    if (workTitle) workTitle.textContent = t('fav.work');
    favHomeAssign.innerHTML = `<span class="fav-row-save-icon" aria-hidden="true">\u2b07</span>`;
    favWorkAssign.innerHTML = `<span class="fav-row-save-icon" aria-hidden="true">\u2b07</span>`;
    favHomeClear.setAttribute('aria-label', t('fav.clearHome'));
    favWorkClear.setAttribute('aria-label', t('fav.clearWork'));
    favSavedHeading.textContent = t('fav.savedList');
    favRecentHeading.textContent = t('fav.recentList');
    favRestoreLabel.textContent = t('fav.last');
    hitFavSheet.setAttribute('aria-label', t('fav.saveAs'));
    for (const btn of hitFavSheet.querySelectorAll<HTMLButtonElement>('[data-fav-choice]')) {
      const choice = btn.dataset.favChoice;
      const span = btn.querySelector<HTMLElement>('[data-i18n-choice]');
      if (!span || !choice) continue;
      if (choice === 'home') span.textContent = t('fav.home');
      else if (choice === 'work') span.textContent = t('fav.work');
      else span.textContent = t('fav.star');
    }
  }

  function makeFavListItem(
    place: SavedPlace,
    opts: { removable?: boolean; savable?: boolean; renameable?: boolean } = {},
  ): HTMLElement {
    const row = document.createElement('div');
    row.className = 'fav-list-item';

    const go = document.createElement('button');
    go.type = 'button';
    go.className = 'fav-list-go';
    go.title = place.label;
    if (place.name?.trim() && place.name.trim() !== place.label) {
      go.innerHTML = '';
      const title = document.createElement('span');
      title.className = 'fav-list-title';
      title.textContent = placeDisplayName(place);
      const sub = document.createElement('span');
      sub.className = 'fav-list-sub';
      sub.textContent = place.label;
      go.append(title, sub);
    } else {
      go.textContent = placeDisplayName(place);
    }
    go.addEventListener('click', () => {
      popoverClose(favPanel);
      pickFavorite(place);
    });
    row.append(go);

    if (opts.savable) {
      const star = document.createElement('button');
      star.type = 'button';
      star.className = 'fav-list-star';
      star.setAttribute('aria-label', t('fav.saveAs'));
      star.title = t('fav.saveAs');
      star.textContent = '\u2605';
      star.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        openHitFavSheet(place, star);
      });
      row.append(star);
    }

    if (opts.renameable) {
      const rename = document.createElement('button');
      rename.type = 'button';
      rename.className = 'fav-list-rename';
      rename.setAttribute('aria-label', t('fav.rename'));
      rename.title = t('fav.rename');
      rename.textContent = '\u270e';
      rename.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        handlers.favoriteRename?.(place);
      });
      row.append(rename);
    }

    if (opts.removable) {
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'fav-list-remove';
      remove.setAttribute('aria-label', t('fav.remove'));
      remove.textContent = '\u00d7';
      remove.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        handlers.favoriteRemove?.(place);
      });
      row.append(remove);
    }
    return row;
  }

  function paintFavorites(): void {
    const fav = loadFavorites();

    const paintSlot = (
      btn: HTMLButtonElement,
      sub: HTMLSpanElement,
      saveBtn: HTMLButtonElement,
      clearBtn: HTMLButtonElement,
      place: SavedPlace | null,
      saveKey: 'fav.saveHome' | 'fav.saveWork',
      updateKey: 'fav.updateHome' | 'fav.updateWork',
    ) => {
      btn.classList.toggle('is-set', place !== null);
      const icon = saveBtn.querySelector<HTMLElement>('.fav-row-save-icon');
      if (place) {
        sub.textContent = place.label;
        sub.hidden = false;
        clearBtn.hidden = false;
        btn.title = place.label;
        if (icon) icon.textContent = '\u21bb';
        saveBtn.setAttribute('aria-label', t(updateKey));
        saveBtn.title = t(updateKey);
      } else {
        sub.textContent = t('fav.emptyHint');
        sub.hidden = false;
        clearBtn.hidden = true;
        btn.title = t('fav.saveHereHint');
        if (icon) icon.textContent = '\u2b07';
        saveBtn.setAttribute('aria-label', t(saveKey));
        saveBtn.title = t(saveKey);
      }
      saveBtn.hidden = false;
    };

    paintSlot(
      favHome,
      favHomeSub,
      favHomeAssign,
      favHomeClear,
      fav.home,
      'fav.saveHome',
      'fav.updateHome',
    );
    paintSlot(
      favWork,
      favWorkSub,
      favWorkAssign,
      favWorkClear,
      fav.work,
      'fav.saveWork',
      'fav.updateWork',
    );

    favSaved.replaceChildren(
      ...(fav.saved.length > 0
        ? fav.saved.map((place) =>
            makeFavListItem(place, { removable: true, renameable: true }),
          )
        : [(() => {
            const empty = document.createElement('div');
            empty.className = 'fav-panel-empty';
            empty.textContent = t('fav.savedEmpty');
            return empty;
          })()]),
    );

    favRecent.replaceChildren(
      ...(fav.recent.length > 0
        ? fav.recent.map((place) => makeFavListItem(place, { savable: true }))
        : [(() => {
            const empty = document.createElement('div');
            empty.className = 'fav-panel-empty';
            empty.textContent = t('fav.recentEmpty');
            return empty;
          })()]),
    );

    const cached = loadLastRoute();
    let restoreWrap = document.getElementById('fav-restore-wrap');
    if (!restoreWrap) {
      restoreWrap = document.createElement('div');
      restoreWrap.id = 'fav-restore-wrap';
      restoreWrap.className = 'fav-restore-wrap';
      favRestore.replaceWith(restoreWrap);
      restoreWrap.append(favRestore);
    }
    favRestore.hidden = !cached;
    favRestore.title = cached
      ? `${t('fav.last')}: ${cached.destination.label}`
      : t('fav.last');

    let lastSave = document.getElementById('fav-restore-save') as HTMLButtonElement | null;
    if (!lastSave) {
      lastSave = document.createElement('button');
      lastSave.type = 'button';
      lastSave.id = 'fav-restore-save';
      lastSave.className = 'fav-list-star';
      lastSave.textContent = '\u2605';
      restoreWrap.append(lastSave);
      lastSave.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const route = loadLastRoute();
        if (!route) return;
        openHitFavSheet(route.destination, lastSave!);
      });
    }
    lastSave.hidden = !cached;
    lastSave.setAttribute('aria-label', t('fav.saveAs'));
    lastSave.title = t('fav.saveAs');
    restoreWrap.hidden = !cached;
  }

  function bindFavoriteSlot(slot: 'home' | 'work'): void {
    const btn = slot === 'home' ? favHome : favWork;
    const assignBtn = slot === 'home' ? favHomeAssign : favWorkAssign;
    const clearBtn = slot === 'home' ? favHomeClear : favWorkClear;

    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const fav = loadFavorites();
      const place = slot === 'home' ? fav.home : fav.work;
      if (!place) {
        setStatus(t('fav.saveHereHint'), 'error');
        return;
      }
      popoverClose(favPanel);
      favToggle.setAttribute('aria-expanded', 'false');
      pickFavorite(place);
    });

    assignBtn.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      handlers.favoriteSet?.(slot);
    });

    clearBtn.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      handlers.favoriteClearSlot?.(slot);
    });
  }

  function placeFavPanel(): void {
    const rect = favToggle.getBoundingClientRect();
    const landscape = document.body.dataset.landscape === '1';
    const railW = topbar ? Math.round(topbar.getBoundingClientRect().width) : 280;
    const width = Math.min(
      landscape ? Math.max(200, railW - 16) : 320,
      window.innerWidth - 24,
    );
    const gap = 6;
    let top = Math.round(rect.bottom + gap);
    let left = Math.round(rect.right - width);
    left = Math.max(12, Math.min(left, window.innerWidth - width - 12));
    const spaceBelow = window.innerHeight - top - 12;
    if (spaceBelow < 160 && rect.top > spaceBelow) {
      top = Math.max(12, Math.round(rect.top - gap - Math.min(280, spaceBelow + rect.height)));
    }
    favPanel.style.top = `${top}px`;
    favPanel.style.left = `${left}px`;
    favPanel.style.right = 'auto';
    favPanel.style.width = `${width}px`;
    favPanel.style.maxHeight = `${Math.max(160, Math.min(480, window.innerHeight - top - 12))}px`;
  }

  function openHitFavSheet(place: SavedPlace, anchor: HTMLElement): void {
    pendingHitPlace = place;
    const rect = anchor.getBoundingClientRect();
    hitFavSheet.style.top = `${Math.round(rect.bottom + 6)}px`;
    hitFavSheet.style.left = `${Math.min(
      Math.round(rect.right - 160),
      window.innerWidth - 172,
    )}px`;
    popoverOpen(hitFavSheet);
  }

  function syncManeuverTop(): void {
    const landscape = document.body.dataset.landscape === '1';
    if (landscape) {
      const topbarHidden = !topbar || topbar.hidden;
      const top = topbarHidden
        ? 8
        : Math.round((topbar.getBoundingClientRect().bottom ?? 56) + 8);
      document.documentElement.style.setProperty(
        '--maneuver-top',
        topbarHidden ? `calc(var(--safe-top) + ${top}px)` : `${top}px`,
      );
      return;
    }
    const topbarHidden = !topbar || topbar.hidden;
    const top = topbarHidden
      ? 8
      : Math.round((topbar.getBoundingClientRect().bottom ?? 56) + 8);
    document.documentElement.style.setProperty(
      '--maneuver-top',
      topbarHidden ? `calc(var(--safe-top) + ${top}px)` : `${top}px`,
    );
  }

  // -------------------------------------------------------------------
  // Form submit → search event
  // -------------------------------------------------------------------
  searchForm.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const q = searchInput.value.trim();
    if (!q) return;
    hideHits();
    handlers.search?.(q);
  });

  profileButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const profile = btn.dataset.profile as Profile | undefined;
      if (!profile) return;
      profileButtons.forEach((b) => {
        const isActive = b === btn;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-selected', String(isActive));
      });
      handlers.changeProfile?.(profile);
    });
  });

  // North-up ⇆ heading-up toggle lives in the bottom HUD during navigation.
  recenterBtn.addEventListener('click', () => handlers.autozoom?.());
  recenterBtn.addEventListener('pointerdown', (ev) => {
    if (ev.button !== 0 && ev.pointerType === 'mouse') return;
    ev.stopPropagation();
  });
  mapCompass.addEventListener('click', () => {
    setViewMode('north');
  });
  mapCompass.addEventListener('pointerdown', (ev) => {
    if (ev.button !== 0 && ev.pointerType === 'mouse') return;
    ev.stopPropagation();
  });
  hudRouteOpts.addEventListener('click', (ev) => {
    ev.stopPropagation();
    setRouteOptsOpen(!routeOptsOpen);
  });
  infoRouteOpts.addEventListener('click', (ev) => {
    ev.stopPropagation();
    setRouteOptsOpen(!routeOptsOpen);
  });

  routeOptsPanel.addEventListener('click', (ev) => ev.stopPropagation());

  for (const btn of routeOptsPanel.querySelectorAll<HTMLButtonElement>('[data-pref]')) {
    btn.addEventListener('click', () => {
      const pref = btn.dataset.pref as RoutePreference | undefined;
      if (!pref || pref === routeOpts.preference) return;
      routeOpts = { ...routeOpts, preference: pref };
      paintRouteOptsPanel();
      emitRouteOptions();
    });
  }

  for (const input of routeOptsPanel.querySelectorAll<HTMLInputElement>('input[data-avoid]')) {
    input.addEventListener('change', () => {
      const feature = input.dataset.avoid as AvoidFeature | undefined;
      if (!feature) return;
      const next = new Set(routeOpts.avoid);
      if (input.checked) next.add(feature);
      else next.delete(feature);
      routeOpts = { ...routeOpts, avoid: [...next] };
      paintRouteOptsPanel();
      emitRouteOptions();
    });
  }

  document.addEventListener('pointerdown', (ev) => {
    if (!routeOptsOpen) return;
    const target = ev.target as Node | null;
    if (!target) return;
    if (routeOptsPanel.contains(target)) return;
    if (hudRouteOpts.contains(target) || infoRouteOpts.contains(target)) return;
    setRouteOptsOpen(false);
  });

  viewButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const mode = btn.dataset.view as ViewMode | undefined;
      if (!mode || (mode !== 'north' && mode !== '2d' && mode !== 'tilted')) return;
      setViewMode(mode);
    });
  });

  infoClear.addEventListener('click', () => handlers.clearRoute?.());
  infoShare.addEventListener('click', () => handlers.shareDestination?.());
  infoAddStop.addEventListener('click', () => handlers.addStop?.());
  infoStops.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement | null)?.closest<HTMLButtonElement>(
      '[data-stop-remove]',
    );
    if (!btn) return;
    const index = Number(btn.dataset.stopRemove);
    if (!Number.isFinite(index)) return;
    handlers.removeStop?.(index);
  });
  favRestore.addEventListener('click', () => {
    popoverClose(favPanel);
    handlers.restoreLastRoute?.();
  });
  bindFavoriteSlot('home');
  bindFavoriteSlot('work');

  favToggle.addEventListener('click', (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (popoverIsOpen(favPanel)) {
      popoverClose(favPanel);
      favToggle.setAttribute('aria-expanded', 'false');
      return;
    }
    paintFavorites();
    placeFavPanel();
    popoverOpen(favPanel);
    favToggle.setAttribute('aria-expanded', 'true');
  });
  favPanel.addEventListener('toggle', (ev) => {
    const te = ev as ToggleEvent;
    favToggle.setAttribute('aria-expanded', String(te.newState === 'open'));
  });

  for (const btn of hitFavSheet.querySelectorAll<HTMLButtonElement>('[data-fav-choice]')) {
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const place = pendingHitPlace;
      const choice = btn.dataset.favChoice;
      popoverClose(hitFavSheet);
      pendingHitPlace = null;
      if (!place || !choice) return;
      if (choice === 'home' || choice === 'work') handlers.favoriteSet?.(choice, place);
      else {
        const name = promptFavoriteName(place);
        if (name === null) return;
        handlers.favoriteSave?.({
          ...place,
          ...(name ? { name } : {}),
        });
      }
    });
  }

  document.addEventListener(
    'pointerdown',
    (ev) => {
      const target = ev.target as Node | null;
      if (!target) return;
      if (popoverIsOpen(favPanel)) {
        if (!favPanel.contains(target) && !favToggle.contains(target)) {
          popoverClose(favPanel);
          favToggle.setAttribute('aria-expanded', 'false');
        }
      }
      if (popoverIsOpen(hitFavSheet) && !hitFavSheet.contains(target)) {
        const hitFavBtn =
          target instanceof Element ? target.closest('.hit-fav') : null;
        if (!hitFavBtn) {
          popoverClose(hitFavSheet);
          pendingHitPlace = null;
        }
      }
    },
    true,
  );

  document.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape') return;
    if (popoverIsOpen(favPanel)) {
      popoverClose(favPanel);
      favToggle.setAttribute('aria-expanded', 'false');
    }
    if (popoverIsOpen(hitFavSheet)) {
      popoverClose(hitFavSheet);
      pendingHitPlace = null;
    }
  });

  powerSaverBtn.addEventListener('click', () => {
    const next = !powerSaverOn;
    applyPowerSaverButton(next);
    handlers.powerSaver?.(next);
  });
  navtoggle.addEventListener('click', () => handlers.navtoggle?.());
  chromeStop.addEventListener('click', () => handlers.navtoggle?.());
  hudOverview.addEventListener('click', () => handlers.overview?.());
  hudExpand.addEventListener('click', () => {
    if (lastNavState === 'navigating' || lastNavState === 'paused') setInfoExpanded(true);
  });
  infoCollapse.addEventListener('click', () => {
    // Collapse allowed in planned (route-preview) AND navigate/paused
    // states. Hidden state refuses the action because there's nothing
    // to collapse yet.
    if (
      lastNavState === 'planned' ||
      lastNavState === 'navigating' ||
      lastNavState === 'paused'
    ) {
      setInfoExpanded(false);
    }
  });
  infoExpand.addEventListener('click', () => {
    if (
      lastNavState === 'planned' ||
      lastNavState === 'navigating' ||
      lastNavState === 'paused'
    ) {
      setInfoExpanded(true);
    }
  });

  searchInput.setAttribute('autocomplete', 'off');
  searchInput.setAttribute('autocapitalize', 'off');
  searchInput.setAttribute('autocorrect', 'off');
  searchInput.setAttribute('spellcheck', 'false');
  searchInput.setAttribute('name', 'navi-q');

  /**
   * Setting input.value while the field is focused makes Safari/iOS
   * prompt "Undo Typing?" / "Visszavonhatod a gépelést?". Blur first,
   * then write — quiet, reliable across WebKit + Chromium.
   */
  function syncSearchClear(): void {
    searchClear.hidden = searchInput.value.length === 0;
  }

  function setSearchValueQuiet(value: string): void {
    if (searchInput.value === value) {
      syncSearchClear();
      return;
    }
    // Keep focus while the user is typing — blur dismisses the soft keyboard.
    const keepFocus = document.activeElement === searchInput;
    searchInput.value = value;
    syncSearchClear();
    if (keepFocus) {
      try {
        searchInput.focus({ preventScroll: true });
      } catch {
        searchInput.focus();
      }
    }
  }

  function blurEditableFields(): void {
    const active = document.activeElement;
    if (
      active instanceof HTMLInputElement ||
      active instanceof HTMLTextAreaElement ||
      (active instanceof HTMLElement && active.isContentEditable)
    ) {
      active.blur();
    }
  }

  function isEditableTarget(target: EventTarget | null): boolean {
    return (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      (target instanceof HTMLElement && target.isContentEditable)
    );
  }

  /** Wipe Safari/iOS undo history for a field (shake-to-undo source). */
  function clearFieldUndoStack(field: HTMLInputElement | HTMLTextAreaElement): void {
    const value = field.value;
    const start = field.selectionStart;
    const end = field.selectionEnd;
    field.value = `${value}\u200b`;
    field.value = value;
    if (typeof start === 'number' && typeof end === 'number') {
      try {
        field.setSelectionRange(start, end);
      } catch {
        /* type=search may reject selection in some engines */
      }
    }
  }

  /**
   * Kill OS “Undo Typing” / shake-to-undo as far as the web platform allows:
   *  1. cancel historyUndo/Redo beforeinput (works without motion permission)
   *  2. block Cmd/Ctrl+Z on editables
   *  3. on strong device motion, clear the field undo stack WITHOUT blurring
   *     (blur would dismiss the mobile soft keyboard mid-typing)
   */
  function installTypingUndoGuard(): void {
    const blockHistoryInput = (ev: Event): void => {
      if (!(ev instanceof InputEvent)) return;
      if (ev.inputType !== 'historyUndo' && ev.inputType !== 'historyRedo') return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
    };
    document.addEventListener('beforeinput', blockHistoryInput, true);

    document.addEventListener(
      'keydown',
      (ev) => {
        if (!(ev.metaKey || ev.ctrlKey)) return;
        if (ev.key !== 'z' && ev.key !== 'Z' && ev.key !== 'y' && ev.key !== 'Y') return;
        if (!isEditableTarget(ev.target)) return;
        ev.preventDefault();
        ev.stopImmediatePropagation();
      },
      true,
    );

    searchInput.addEventListener('blur', () => {
      clearFieldUndoStack(searchInput);
    });

    let lastShakeAt = 0;
    let lastAx = 0;
    let lastAy = 0;
    let lastAz = 0;
    let haveSample = false;

    const onMotion = (ev: DeviceMotionEvent): void => {
      const active = document.activeElement;
      if (!isEditableTarget(active)) {
        haveSample = false;
        return;
      }
      // Never blur while typing — that dismisses the mobile keyboard.
      // OS shake-to-undo is blocked via beforeinput (historyUndo) above.
      const raw = ev.acceleration;
      if (!raw) return;
      const x = raw.x ?? 0;
      const y = raw.y ?? 0;
      const z = raw.z ?? 0;
      const mag = Math.hypot(x, y, z);
      let jerk = 0;
      if (haveSample) {
        jerk = Math.hypot(x - lastAx, y - lastAy, z - lastAz);
      }
      lastAx = x;
      lastAy = y;
      lastAz = z;
      haveSample = true;
      if (!(mag > 4.5 || jerk > 6)) return;
      const now = Date.now();
      if (now - lastShakeAt < 800) return;
      lastShakeAt = now;
      if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
        clearFieldUndoStack(active);
      }
      try {
        ev.preventDefault();
      } catch {
        /* some browsers reject preventDefault on motion */
      }
    };
    window.addEventListener('devicemotion', onMotion, { passive: false });

    const Motion = DeviceMotionEvent as unknown as {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    };
    if (typeof Motion.requestPermission === 'function') {
      const ask = (): void => {
        void Motion.requestPermission?.().catch(() => {
          /* user denied — beforeinput guard still applies */
        });
      };
      searchInput.addEventListener('focus', ask, { once: true });
      searchInput.addEventListener('touchstart', ask, { once: true, passive: true });
    }
  }

  searchClear.addEventListener('click', (ev) => {
    ev.preventDefault();
    if (searchTimer !== null) window.clearTimeout(searchTimer);
    inflight?.abort();
    setSearchValueQuiet('');
    hideHits();
    searchInput.focus();
  });

  function setChromeHiddenForNav(hidden: boolean): void {
    searchForm.hidden = hidden;
    if (profilesEl) profilesEl.hidden = hidden;
    if (topbar) topbar.classList.toggle('is-nav-compact', hidden);
    if (hidden) {
      hideHits();
      popoverClose(favPanel);
      popoverClose(hitFavSheet);
      if (document.activeElement === searchInput) searchInput.blur();
    }
    requestAnimationFrame(() => syncManeuverTop());
  }
  let inflight: AbortController | null = null;
  let searchTimer: number | null = null;
  let lastGeocodeAt = 0;

  async function runLiveGeocode(q: string): Promise<void> {
    inflight?.abort();
    inflight = new AbortController();
    lastGeocodeAt = Date.now();
    try {
      const hits = await geocode(q, inflight.signal);
      renderHits(hits);
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      hideHits();
      setStatus((err as Error).message, 'error');
    }
  }

  searchInput.addEventListener('input', () => {
    syncSearchClear();
    const q = searchInput.value.trim();
    if (searchTimer !== null) window.clearTimeout(searchTimer);
    if (q.length < 3) {
      hideHits();
      return;
    }
    // Debounce typing, then schedule so a soft 1.5 s cap delays instead
    // of silently dropping the query (that left street searches empty).
    searchTimer = window.setTimeout(() => {
      const wait = Math.max(0, 1500 - (Date.now() - lastGeocodeAt));
      if (wait > 0) {
        searchTimer = window.setTimeout(() => {
          void runLiveGeocode(q);
        }, wait);
        return;
      }
      void runLiveGeocode(q);
    }, 500);
  });

  function renderHits(hits: GeocodeHit[]): void {
    if (hits.length === 0) {
      hideHits();
      return;
    }
    placeHitsUnderTopbar();
    hitsBox.replaceChildren(
      ...hits.map((hit) => {
        const item = document.createElement('div');
        item.className = 'hit';
        item.setAttribute('role', 'option');

        const selectBtn = document.createElement('button');
        selectBtn.type = 'button';
        selectBtn.className = 'hit-select';
        const label = document.createElement('span');
        label.className = 'hit-label';
        label.textContent = hit.name;
        selectBtn.append(label);
        if (hit.layer === 'street' || hit.layer === 'address') {
          const badge = document.createElement('span');
          badge.className = 'hit-badge';
          badge.textContent = hit.layer === 'address' ? '#' : '\u301c';
          badge.title = hit.layer;
          selectBtn.append(badge);
        } else if (hit.layer === 'amenity') {
          const badge = document.createElement('span');
          badge.className = 'hit-badge';
          badge.textContent = '\u25c9';
          badge.title = 'POI';
          selectBtn.append(badge);
        }

        const starBtn = document.createElement('button');
        starBtn.type = 'button';
        starBtn.className = 'hit-fav';
        starBtn.setAttribute('aria-label', t('fav.saveAs'));
        starBtn.title = t('fav.saveAs');
        starBtn.textContent = '\u2605';

        const selectHit = (ev: Event) => {
          ev.preventDefault();
          ev.stopPropagation();
          hideHits();
          popoverClose(hitFavSheet);
          if (isPlaceLayer(hit.layer)) {
            searchInput.value = `${hit.name}, `;
            syncSearchClear();
            searchInput.focus();
            const len = searchInput.value.length;
            searchInput.setSelectionRange(len, len);
            handlers.searchResult?.(hit);
            return;
          }
          setSearchValueQuiet(hit.name);
          handlers.searchResult?.(hit);
        };

        selectBtn.addEventListener('pointerdown', (ev) => {
          ev.preventDefault();
        });
        selectBtn.addEventListener('click', selectHit);

        starBtn.addEventListener('pointerdown', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
        });
        starBtn.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          openHitFavSheet(
            {
              lat: hit.location.lat,
              lng: hit.location.lng,
              label: hit.name,
            },
            starBtn,
          );
        });

        item.append(selectBtn, starBtn);
        return item;
      }),
    );
    hitsBox.hidden = false;
  }

  function hideHits(): void {
    hitsBox.hidden = true;
    hitsBox.replaceChildren();
    popoverClose(hitFavSheet);
  }

  function pickFavorite(place: SavedPlace): void {
    if (searchTimer !== null) window.clearTimeout(searchTimer);
    inflight?.abort();
    hideHits();
    setSearchValueQuiet(placeDisplayName(place));
    handlers.favoriteGo?.(place);
  }

  function isPlaceLayer(layer: string): boolean {
    return (
      layer === 'locality' ||
      layer === 'localadmin' ||
      layer === 'neighbourhood' ||
      layer === 'borough' ||
      layer === 'county' ||
      layer === 'region'
    );
  }

  function setStatus(message: string, tone: 'info' | 'error' = 'info'): void {
    infoMessage.textContent = message;
    infoMessage.classList.toggle('is-error', tone === 'error');
  }

  // -------------------------------------------------------------------
  // Panel-vs-HUD layout state machine
  // -------------------------------------------------------------------
  let navState: NavState = 'hidden';
  let infoExpanded = false;
  let itineraryWanted = false;

  function applyPanelLayout(): void {
    setChromeHiddenForNav(navState === 'navigating' || navState === 'paused');
    if (navState === 'navigating' || navState === 'paused') {
      blurEditableFields();
    }
    if (navState === 'hidden') {
      info.hidden = true;
      hud.hidden = true;
      info.classList.remove('panel-collapsed');
      hud.classList.remove('paused');
      infoCollapse.hidden = true;
      syncBodyChromeFlags();
      syncRecenterVisibility();
      notifyLayout();
      return;
    }
    if (navState === 'planned') {
      info.hidden = false;
      hud.hidden = true;
      hud.classList.remove('paused');
      if (infoExpanded === false) {
        info.classList.add('panel-planned-min');
        info.classList.remove('panel-collapsed');
        infoSteps.hidden = true;
        infoItinerary.hidden = true;
        infoCollapse.hidden = true;
        infoExpand.hidden = false;
      } else {
        info.classList.remove('panel-planned-min');
        info.classList.remove('panel-collapsed');
        infoSteps.hidden = false;
        infoItinerary.hidden = !itineraryWanted;
        infoCollapse.hidden = false;
        infoExpand.hidden = true;
      }
      syncBodyChromeFlags();
      syncRecenterVisibility();
      notifyLayout();
      return;
    }
    if (navState === 'paused') {
      hud.classList.add('paused');
    } else {
      hud.classList.remove('paused');
    }
    if (infoExpanded) {
      info.hidden = false;
      info.classList.remove('panel-collapsed');
      hud.hidden = true;
      infoSteps.hidden = false;
      infoCollapse.hidden = false;
      infoExpand.hidden = true;
      hudExpand.setAttribute('aria-expanded', 'true');
    } else {
      info.hidden = false;
      info.classList.add('panel-collapsed');
      hud.hidden = false;
      infoCollapse.hidden = true;
      infoExpand.hidden = true;
      hudExpand.setAttribute('aria-expanded', 'false');
    }
    syncBodyChromeFlags();
    syncRecenterVisibility();
    notifyLayout();
  }

  function notifyLayout(): void {
    requestAnimationFrame(() => {
      syncFabLift();
      requestAnimationFrame(() => {
        syncFabLift();
        handlers.layout?.();
      });
    });
  }

  /**
   * Mirror panel/HUD visibility onto body data-* flags so layout CSS can
   * rely on simple attribute selectors instead of :has().
   */
  function syncBodyChromeFlags(): void {
    const hudOpen = !hud.hidden;
    const infoOpen = !info.hidden && !info.classList.contains('panel-collapsed');
    const stepsOpen = infoOpen && !infoSteps.hidden;
    document.body.dataset.bottomChrome = hudOpen ? 'hud' : infoOpen ? 'info' : 'none';
    document.body.dataset.stepsOpen = stepsOpen ? '1' : '0';
    info.classList.toggle(
      'is-tall',
      infoSteps.childElementCount > 0 && !infoSteps.hidden,
    );
  }

  function syncRecenterVisibility(): void {
    const infoOpen = !info.hidden && !info.classList.contains('panel-collapsed');
    const stepsCoverMap = infoOpen && !infoSteps.hidden;
    const landscape = document.body.dataset.landscape === '1';
    // Portrait planned preview covers the map corner; landscape keeps the
    // FAB on the free map half beside the rail.
    const plannedPreview = navState === 'planned' && infoOpen && !landscape;
    recenterBtn.hidden = stepsCoverMap || plannedPreview;
    syncFabLift();
  }

  function syncFabLift(): void {
    const landscape = document.body.dataset.landscape === '1';
    let chromeH = 0;

    if (!landscape) {
      if (!hud.hidden) {
        const h = hud.getBoundingClientRect().height;
        if (Number.isFinite(h) && h > 0) chromeH = Math.max(chromeH, h);
      }

      if (!info.hidden && !info.classList.contains('panel-collapsed')) {
        const h = info.getBoundingClientRect().height;
        if (Number.isFinite(h) && h > 0) chromeH = Math.max(chromeH, h);
      }
    }

    document.documentElement.style.setProperty(
      '--bottom-chrome-h',
      `${String(Math.round(chromeH))}px`,
    );

    const stackPx = !mapCompass.hidden && !recenterBtn.hidden ? 52 : 0;
    document.documentElement.style.setProperty('--compass-stack', `${String(stackPx)}px`);

    const fabSteps =
      (!recenterBtn.hidden ? 1 : 0) + (!mapCompass.hidden ? 1 : 0);
    document.documentElement.style.setProperty(
      '--weather-stack',
      `${String(fabSteps * 52)}px`,
    );

    // Preference popover sits above the bottom chrome — lift FABs/weather over it.
    let optsExtra = 0;
    if (!landscape && routeOptsOpen && !routeOptsPanel.hidden) {
      const rh = routeOptsPanel.getBoundingClientRect().height;
      if (Number.isFinite(rh) && rh > 0) optsExtra = Math.round(rh + 12);
    }

    const liftExtra = Math.round(chromeH + optsExtra);
    if (landscape || liftExtra <= 0) {
      document.documentElement.style.setProperty(
        '--fab-lift',
        'calc(16px + var(--safe-bottom))',
      );
      return;
    }

    document.documentElement.style.setProperty(
      '--fab-lift',
      `calc(16px + var(--safe-bottom) + ${String(liftExtra)}px)`,
    );
  }

  function setInfoExpanded(next: boolean): void {
    infoExpanded = next;
    applyPanelLayout();
    handlers.panelToggle?.(next);
  }

  /**
   * Switch between the two view modes. Called by both:
   *   1. The topbar `.view-btn[data-view]` click handler (user-driven).
   *   2. main.ts on boot, after reading the persisted preference.
   *
   * In both cases we apply the visual layout, persist (when the user is
   * the source), and emit the `viewmode` event so main.ts can drive
   * the MapLibre camera + the persistence layer.
   */
  function setViewMode(mode: ViewMode): void {
    applyViewModeButton(mode);
    handlers.viewmode?.(mode);
  }

  function setArrowBearing(bearingDeg: number): void {
    if (!mapCompassRose || mapCompass.hidden) return;
    if (!Number.isFinite(bearingDeg)) return;
    const deg = ((bearingDeg % 360) + 360) % 360;
    mapCompassRose.style.transform = `rotate(${(-deg).toFixed(1)}deg)`;
  }

  // -------------------------------------------------------------------
  // Initial paint + subscribe to lang changes.
  // -------------------------------------------------------------------
  applyStaticLabels();
  paintFavorites();
  onLangChange(() => applyStaticLabels());
  window.addEventListener('resize', () => {
    syncLandscapeLayout();
    if (!hitsBox.hidden) placeHitsUnderTopbar();
    syncManeuverTop();
    syncFabLift();
  });
  window.addEventListener('orientationchange', () => {
    syncLandscapeLayout();
    window.setTimeout(() => syncLandscapeLayout(), 80);
    window.setTimeout(() => syncLandscapeLayout(), 320);
  });
  syncLandscapeLayout();
  syncManeuverTop();
  syncFabLift();
  installTypingUndoGuard();

  if (typeof ResizeObserver !== 'undefined') {
    const chromeObserver = new ResizeObserver(() => {
      syncFabLift();
      syncManeuverTop();
    });
    chromeObserver.observe(hud);
    chromeObserver.observe(info);
    if (topbar) chromeObserver.observe(topbar);
    const mapEl = document.getElementById('map');
    if (mapEl) {
      const mapObserver = new ResizeObserver(() => {
        notifyLayout();
      });
      mapObserver.observe(mapEl);
    }
  }

  return {
    setRoute(_coords, distance, duration, steps) {
      blurEditableFields();
      info.hidden = false;
      infoDistance.textContent = formatDistance(distance);
      infoDuration.textContent = formatDuration(duration);
      infoSteps.replaceChildren();
      setStatus(t('status.routeReady'));
      syncBodyChromeFlags();
      syncRecenterVisibility();
      notifyLayout();

      const token = ++stepsPaintToken;
      const batchSize = 40;
      void (async () => {
        for (let i = 0; i < steps.length; i += batchSize) {
          if (token !== stepsPaintToken) return;
          const frag = document.createDocumentFragment();
          const end = Math.min(i + batchSize, steps.length);
          for (let j = i; j < end; j++) {
            const s = steps[j]!;
            const li = document.createElement('li');
            const instr = document.createElement('span');
            instr.className = 'step-instr';
            instr.textContent = s.instruction || s.name || '\u2014';
            const dist = document.createElement('span');
            dist.className = 'step-distance';
            dist.textContent = formatDistance(s.distance);
            li.append(instr, dist);
            frag.append(li);
          }
          infoSteps.append(frag);
          await yieldToMain();
        }
        if (token === stepsPaintToken) {
          syncBodyChromeFlags();
          syncRecenterVisibility();
          notifyLayout();
        }
      })();
    },
    clearRoute() {
      stepsPaintToken += 1;
      info.hidden = true;
      infoSteps.hidden = false; // re-arm for next plan, in case user collapsed earlier
      infoSteps.replaceChildren();
      infoItinerary.hidden = true;
      infoStops.replaceChildren();
      addingStopUi = false;
      infoAddStop.setAttribute('aria-pressed', 'false');
      infoAddStop.classList.remove('is-armed');
      infoAddStop.disabled = false;
      infoAddStop.textContent = t('info.addStop');
      searchInput.placeholder = t('search.placeholder');
      infoDistance.textContent = '\u2014';
      infoDuration.textContent = '\u2014';
      infoMessage.textContent = '';
      setSearchValueQuiet('');
      hideHits();
      hudSpeed.textContent = '\u2014';
      hudDistance.textContent = '\u2014';
      hudDuration.textContent = '\u2014';
      hudEta.textContent = '\u2014';
      hudNextInstr.textContent = '';
      hudNextName.textContent = '';
      hudNextName.hidden = true;
      hudNextDist.textContent = '';
      hudNextArrow.replaceChildren();
      hudNext.hidden = true;
      hudLaneHint.hidden = true;
      hudLaneHint.textContent = '';
      hudThen.hidden = true;
      hudThenInstr.textContent = '';
      hudThenArrow.replaceChildren();
      applyHudAmenities({});
      lastHudData = null;
      lastHudPaintSig = '';
      lastAmenityPaintSig = '';
      navState = 'hidden';
      lastNavState = 'hidden';
      infoExpanded = false;
      document.body.dataset.navState = 'hidden';
      document.body.dataset.viewMode = 'north';
      userArrow.hidden = true;
      hudLimit.hidden = true;
      hudLimit.textContent = '';
      hudRadar.hidden = true;
      hudRadarText.textContent = '';
      hudRadarDist.textContent = '';
      hudSpeed.classList.remove('is-overspeed');
      hudRoadworkReset();
      // Clear chip caches so applyStaticLabels doesn't replay stale data.
      lastRadar = null;
      lastRoadwork = null;
      lastSpeedLimitKmh = null;
      lastOverspeed = false;
      applyPanelLayout();
      applyNavToggleButton('hidden');
      syncRecenterVisibility();
    },
    setStatus,
    setNavState(state) {
      navState = state;
      document.body.dataset.navState = state;
      setChromeHiddenForNav(state === 'navigating' || state === 'paused');
      if (state === 'navigating') infoExpanded = false;
      applyNavToggleButton(state);
      applyHudOverviewButton();
      if (!(state === 'navigating' || state === 'paused')) {
        hudNext.hidden = true;
      }
      if (!(state === 'navigating' || state === 'paused' || state === 'planned')) {
        setRouteOptsOpen(false);
      }
      applyPanelLayout();
      syncManeuverTop();
    },
    setInfoExpanded,
    setHudData(data) {
      lastHudData = data;
      applyHudData(data);
    },
    setSpeedLimit(kmh) {
      lastSpeedLimitKmh = kmh;
      applySpeedLimitFromCache();
    },
    setHudRadar(parts) {
      lastRadar = parts;
      applyRadarChipFromCache();
    },
    setRoadwork(kind, distanceM) {
      lastRoadwork = { kind, distanceM };
      applyRoadworkChip(kind, distanceM);
    },
    setOverspeed(isOver) {
      lastOverspeed = !!isOver;
      hudSpeed.classList.toggle('is-overspeed', lastOverspeed);
    },
    setViewMode,
    setAutoZoomActive(active) {
      applyAutoZoomButton(active);
    },
    setRouteOptionsUi(opts, profile) {
      routeOpts = {
        preference: opts.preference,
        avoid: [...opts.avoid],
      };
      routeOptsProfile = profile;
      paintRouteOptsPanel();
    },
    setArrowBearing,
    setWeather(data) {
      weatherChip.hidden = false;
      weatherChip.removeAttribute('hidden');
      if (!data) {
        weatherTemp.textContent = '…°';
        weatherWindSpeed.textContent = '—';
        weatherWindArrow.style.transform = '';
        syncFabLift();
        return;
      }
      weatherTemp.textContent = data.tempLabel;
      weatherWindSpeed.textContent = data.windLabel;
      weatherWindArrow.style.transform = `rotate(${data.windTowardDeg.toFixed(0)}deg)`;
      syncFabLift();
    },
    refreshFavorites() {
      paintFavorites();
    },
    setRestoreVisible(visible) {
      favRestore.hidden = !visible;
    },
    setHudAmenities(next) {
      applyHudAmenities(next);
    },
    setPowerSaverUi(on) {
      applyPowerSaverButton(on);
    },
    setSearchQuery(value) {
      setSearchValueQuiet(value);
    },
    setStopsUi(stops, opts) {
      const show = stops.length > 0 && navState === 'planned';
      itineraryWanted = show;
      addingStopUi = opts.addingStop;
      infoItinerary.hidden = !show || info.classList.contains('panel-planned-min');
      infoAddStop.hidden = !show;
      infoAddStop.disabled = !opts.canAdd && !opts.addingStop;
      infoAddStop.setAttribute('aria-pressed', opts.addingStop ? 'true' : 'false');
      infoAddStop.classList.toggle('is-armed', opts.addingStop);
      infoAddStop.textContent = opts.addingStop ? t('info.addStopArmed') : t('info.addStop');
      searchInput.placeholder = opts.addingStop
        ? t('search.addStopPlaceholder')
        : t('search.placeholder');
      searchForm.setAttribute('aria-label', searchInput.placeholder);
      if (opts.addingStop) {
        setSearchValueQuiet('');
      }

      if (!show) {
        infoStops.replaceChildren();
        return;
      }

      infoStops.replaceChildren(
        ...stops.map((stop, index) => {
          const li = document.createElement('li');
          li.className = 'info-stop';
          const num = document.createElement('span');
          num.className = 'info-stop-num';
          num.textContent = String(index + 1);
          const body = document.createElement('div');
          body.className = 'info-stop-body';
          const role = document.createElement('span');
          role.className = 'info-stop-role';
          role.textContent =
            index === stops.length - 1 ? t('info.stopFinal') : t('info.stopVia');
          const label = document.createElement('span');
          label.className = 'info-stop-label';
          label.textContent = stop.label;
          body.append(role, label);
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'info-stop-remove';
          remove.dataset.stopRemove = String(index);
          remove.setAttribute('aria-label', t('info.removeStop'));
          remove.textContent = '×';
          li.append(num, body, remove);
          return li;
        }),
      );
      notifyLayout();
    },
    setPowerDim(on) {
      applyPowerDim(on);
    },
    on(event, handler) {
      handlers[event] = handler;
    },
  };
}

// ---------------------------------------------------------------------------
// Re-export the language-aware formatters so pre-existing imports from
// ui.ts (other modules or future tests) keep working.
export { formatDistance, formatDuration, formatSpeed, formatEtaClock };
